import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Search, School, MapPin, Building2 } from 'lucide-react';

const AdminSchoolReference = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterNew, setFilterNew] = useState('');

  const loadSchools = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterCity.trim()) params.city_regency = filterCity.trim();
      if (filterNew !== '') params.is_new = filterNew === 'true';
      const res = await apiService.admin.getSchoolReference(params);
      setSchools(res.data || []);
    } catch (err) {
      console.error('Failed to load school reference', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadSchools();
  };

  const cities = [...new Set(schools.map((s) => s.city_regency).filter(Boolean))].sort();
  const newCount = schools.filter((s) => s.is_new).length;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Referensi Sekolah Peserta</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Data sekolah/tim peserta — total {schools.length} sekolah
            {newCount > 0 && (
              <span className="ml-2 text-green-600 font-medium">({newCount} peserta baru)</span>
            )}
          </p>
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch} className="bg-white rounded-xl shadow p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cari nama sekolah..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="sm:w-52">
            <input
              type="text"
              placeholder="Filter kota/kabupaten..."
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="sm:w-40">
            <select
              value={filterNew}
              onChange={(e) => setFilterNew(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Semua peserta</option>
              <option value="true">Peserta baru</option>
              <option value="false">Peserta lama</option>
            </select>
          </div>
          <button
            type="submit"
            className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
          >
            Cari
          </button>
        </form>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Memuat data...</div>
        ) : schools.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">
            Tidak ada data ditemukan.
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">No</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Nama Sekolah / Tim</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Provinsi</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kota / Kabupaten</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kecamatan</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {schools.map((school, idx) => (
                    <tr key={school.id || idx} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-400 w-10">{school.source_no ?? idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <School className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="font-medium text-gray-900">{school.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5 text-gray-400" />
                          {school.province || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-gray-400" />
                          {school.city_regency || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{school.district || '-'}</td>
                      <td className="px-4 py-3">
                        {school.is_new ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            Baru
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                            Lama
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSchoolReference;
